import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { CLINIC } from "@/data/content";

function LegalShell({ title, crumb, seoTitle, children }) {
  useEffect(() => {
    document.title = seoTitle || `${title} | MEDAS`;
    return () => { document.title = "MEDAS Timișoara | Laborator Analize Medicale & Clinică"; };
  }, [title, seoTitle]);
  return (
    <>
      <section className="pt-28 lg:pt-36 pb-10 champagne-gradient">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-[#8A7A5E] mb-4">
            <Link to="/" className="hover:text-[#A6823B]">Acasă</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#4A3F2E] font-medium">{crumb}</span>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-[#2A2521]" data-testid="legal-title">{title}</h1>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-5 text-[#4A3F2E] leading-relaxed [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-[#2A2521] [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-[#A6823B] [&_a]:underline">
          {children}
        </div>
      </section>
    </>
  );
}

const P = ({ children }) => <p>{children}</p>;

export function PoliticaConfidentialitate() {
  return (
    <LegalShell title="Politica de confidențialitate" crumb="Politica de confidențialitate"
      seoTitle="Politica de confidențialitate | MEDAS by ExcelLab">
      <P><strong>Excellab S.R.L.</strong> (MEDAS by ExcelLab), în calitate de operator de date cu caracter personal, tratează cu maximă seriozitate datele dumneavoastră, în conformitate cu Regulamentul (UE) 2016/679 (GDPR) și legislația română aplicabilă. Vă încurajăm să citiți cu atenție această notă de informare.</P>

      <h2>1. Ce date personale prelucrăm</h2>
      <P>Prelucrăm datele obținute direct de la dumneavoastră sau rezultate în urma prestării serviciilor medicale, inclusiv:</P>
      <ul>
        <li><strong>Date medicale</strong> (date sensibile): simptome, istoric medical, grupă sangvină, analizele efectuate și rezultatele acestora, recomandări și tratamente, date biometrice.</li>
        <li><strong>Detalii personale</strong>: nume, prenume, sex, data nașterii, cetățenie, CNP și datele din actul de identitate, înregistrări video (camere de supraveghere semnalizate).</li>
        <li><strong>Detalii de contact</strong>: adresă, telefon, e-mail.</li>
        <li><strong>Detalii de plată</strong>: date de facturare, cont/card bancar (IBAN), titular.</li>
        <li><strong>Detalii din Cardul Național de Sănătate, detalii profesionale și de asigurare</strong>.</li>
        <li><strong>Opinii și sesizări, istoric de interacțiune</strong> și date tehnice (IP, browser, tip terminal la accesarea aplicațiilor web).</li>
      </ul>

      <h2>2. Sursele datelor</h2>
      <P>De regulă, ni le comunicați direct când beneficiați de serviciile noastre. Pentru medicina muncii sau abonamente/asigurări, unele date le primim de la angajator sau asigurător. Le folosim și la efectuarea programărilor prin telefon, e-mail, WhatsApp sau alte aplicații web.</P>

      <h2>3. Temeiurile legale</h2>
      <P>Prelucrăm datele pentru prestarea serviciilor medicale și executarea contractului cu dumneavoastră, în baza consimțământului (pentru marketing), pentru îndeplinirea obligațiilor legale și în temeiul interesului legitim. Datele sensibile sunt prelucrate în scopuri de asistență medicală, diagnostic, evaluarea capacității de muncă, situații de urgență sau interes public în domeniul sănătății.</P>

      <h2>4. Scopurile prelucrării</h2>
      <P>Prestarea și înregistrarea serviciilor medicale, comunicarea cu dumneavoastră, programări, informarea privind rezultatele, comunicări de marketing (cu acordul dumneavoastră), gestiunea IT și financiară, soluționarea litigiilor, sondaje, îmbunătățirea calității serviciilor și supraveghere video conform legii.</P>

      <h2>5. Cui divulgăm datele</h2>
      <P>Ca regulă, nu divulgăm datele. În anumite situații le putem transmite către: medici colaboratori și furnizori de servicii medicale, angajator (doar în limita legală pentru medicina muncii), autorități publice (CNAS, Ministerul Sănătății etc.), asigurători, consultanți profesionali și persoane împuternicite (servicii soft medical, plăți, arhivare) — toți obligați la confidențialitate.</P>

      <h2>6. Transferuri internaționale</h2>
      <P>În prezent nu transferăm datele dumneavoastră către state terțe sau organizații internaționale. Dacă va fi necesar, vă vom informa în prealabil.</P>

      <h2>7. Cât timp stocăm datele</h2>
      <P>Stocăm datele pentru o perioadă cuprinsă între 6 luni și 100 de ani în cazul documentelor medicale, pe servere proprii sau ale companiilor specializate în arhivare.</P>

      <h2>8. Securitatea datelor</h2>
      <P>Aplicăm politici dedicate, minimizarea datelor, restrângerea accesului, măsuri tehnice (criptare) și audituri periodice de securitate. La accesarea rezultatelor online vă autentificați folosind codul web și codul de probă. Ne instruim constant personalul și anonimizăm datele acolo unde este posibil.</P>

      <h2>9. Drepturile dumneavoastră</h2>
      <ul>
        <li>Dreptul de acces la date;</li>
        <li>Dreptul la rectificare;</li>
        <li>Dreptul la ștergere („dreptul de a fi uitat");</li>
        <li>Dreptul la restricționarea prelucrării;</li>
        <li>Dreptul de a obiecta;</li>
        <li>Dreptul la portabilitatea datelor;</li>
        <li>Dreptul de a retrage consimțământul;</li>
        <li>Dreptul de a depune o plângere la ANSPDCP.</li>
      </ul>
      <P>Pentru exercitarea acestor drepturi ne puteți contacta la <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a> sau la telefon <a href={CLINIC.phoneRaw}>{CLINIC.phone}</a>.</P>

      <h2>10. Consecințele nefurnizării datelor</h2>
      <P>Nu aveți obligația de a ne furniza datele, însă fără acestea nu vom putea presta serviciile solicitate.</P>

      <h2>11. Inexistența unui proces decizional automatizat</h2>
      <P>Nu luăm decizii bazate exclusiv pe prelucrarea automată a datelor (inclusiv profilare) care să producă efecte juridice asupra dumneavoastră.</P>

      <h2>12. Contact</h2>
      <P>Operator: Excellab S.R.L. (MEDAS by ExcelLab), {CLINIC.address}. E-mail: <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>, Telefon: <a href={CLINIC.phoneRaw}>{CLINIC.phone}</a>.</P>
    </LegalShell>
  );
}

export function PoliticaCookies() {
  return (
    <LegalShell title="Politica de cookie-uri" crumb="Politica de cookie-uri"
      seoTitle="Politica de cookie-uri | MEDAS by ExcelLab">
      <P>Acest website folosește cookie-uri pentru a asigura funcționarea corectă a site-ului și pentru a îmbunătăți experiența de navigare, în conformitate cu legislația privind protecția datelor (GDPR) și cu Directiva ePrivacy.</P>

      <h2>Ce sunt cookie-urile</h2>
      <P>Cookie-urile sunt fișiere text de mici dimensiuni stocate de browser pe dispozitivul dumneavoastră atunci când vizitați un website. Ele permit site-ului să rețină anumite informații pe durata vizitei sau la vizite ulterioare.</P>

      <h2>Ce tipuri de cookie-uri folosim</h2>
      <ul>
        <li><strong>Cookie-uri esențiale</strong> — necesare pentru funcționarea site-ului (ex. reținerea preferinței privind consimțământul). Acestea nu pot fi dezactivate.</li>
        <li><strong>Cookie-uri de performanță/analiză</strong> — ne ajută să înțelegem cum este utilizat site-ul, pentru a-l îmbunătăți. Sunt activate doar cu acordul dumneavoastră.</li>
      </ul>

      <h2>Gestionarea cookie-urilor</h2>
      <P>La prima vizită puteți accepta toate cookie-urile sau doar pe cele esențiale, prin bannerul de consimțământ. Puteți oricând modifica sau șterge cookie-urile din setările browserului dumneavoastră. Dezactivarea anumitor cookie-uri poate afecta funcționarea site-ului.</P>

      <h2>Contact</h2>
      <P>Pentru întrebări legate de politica de cookie-uri, ne puteți contacta la <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a> sau la <a href={CLINIC.phoneRaw}>{CLINIC.phone}</a>.</P>
    </LegalShell>
  );
}

export function TermeniConditii() {
  return (
    <LegalShell title="Termeni și condiții" crumb="Termeni și condiții"
      seoTitle="Termeni și condiții | MEDAS by ExcelLab">
      <P>Prin utilizarea acestui website (operat de Excellab S.R.L. – MEDAS by ExcelLab) sunteți de acord cu termenii și condițiile de mai jos. Vă rugăm să îi citiți cu atenție.</P>

      <h2>1. Rolul website-ului</h2>
      <P>Acest site are caracter informativ și prezintă serviciile clinicii și ale laboratorului de analize. Programările se fac telefonic la <a href={CLINIC.phoneRaw}>{CLINIC.phone}</a>. Informațiile de pe site nu constituie sfat medical și nu înlocuiesc consultația de specialitate.</P>

      <h2>2. Prețuri și servicii</h2>
      <P>Prețurile analizelor și pachetelor afișate au caracter informativ și pot fi actualizate. Prețul final se confirmă la programare sau la recepția clinicii.</P>

      <h2>3. Rezultatele analizelor</h2>
      <P>Accesarea rezultatelor online se face pe platforma securizată a laboratorului, prin autentificare cu codul web și codul de probă furnizate la recoltare.</P>

      <h2>4. Proprietate intelectuală</h2>
      <P>Conținutul site-ului (texte, imagini, logo, design) aparține MEDAS / Excellab S.R.L. și nu poate fi reprodus fără acordul scris al proprietarului.</P>

      <h2>5. Protecția datelor</h2>
      <P>Prelucrarea datelor cu caracter personal se realizează conform <Link to="/politica-confidentialitate">Politicii de confidențialitate</Link>.</P>

      <h2>6. Modificări</h2>
      <P>Ne rezervăm dreptul de a modifica acești termeni. Versiunea actualizată va fi publicată pe această pagină.</P>

      <h2>7. Contact</h2>
      <P>Excellab S.R.L. (MEDAS by ExcelLab), {CLINIC.address}. E-mail: <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>, Telefon: <a href={CLINIC.phoneRaw}>{CLINIC.phone}</a>.</P>
    </LegalShell>
  );
}
